import { useContext, useState } from "react";
import { Button, Col, Divider, Form, Input, Modal, Row, Typography } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCircleInfo, faXmark } from "@fortawesome/free-solid-svg-icons";
import { authService } from "../../services/authService";
import styles from "./settings.module.scss";
import { AuthContext } from "../../context/AuthProvider";

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateUserModal: React.FC<Props> = ({ open, onClose, onSuccess }) => {
  const { user } = useContext(AuthContext);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | string[] | null>(null);
  const [isPasswordTouched, setIsPasswordTouched] = useState(false);
  const [passwordRules, setPasswordRules] = useState({
    length: false, lowercase: false, uppercase: false, number: false, special: false,
  });
  const [passwordMatch, setPasswordMatch] = useState<boolean | null>(null);

  const handlePasswordChange = (value: string) => {
    setIsPasswordTouched(true);
    setPasswordRules({
      length: value.length >= 6,
      lowercase: /[a-z]/.test(value),
      uppercase: /[A-Z]/.test(value),
      number: /\d/.test(value),
      special: /[@$!%*?&]/.test(value),
    });
    const confirm = form.getFieldValue("confirmPassword");
    if (confirm) setPasswordMatch(value === confirm);
  };

  const handleConfirmChange = (value: string) => {
    setPasswordMatch(value ? value === form.getFieldValue("temporaryPassword") : null);
  };

  const getIcon = (rule: boolean) => (!isPasswordTouched ? faXmark : rule ? faCheck : faXmark);
  const getRuleClass = (rule: boolean) => (!isPasswordTouched ? styles.ruleDefault : rule ? styles.ruleValid : styles.ruleInvalid);

  const handleClose = () => {
    form.resetFields();
    setError(null);
    setIsPasswordTouched(false);
    setPasswordRules({ length: false, lowercase: false, uppercase: false, number: false, special: false });
    setPasswordMatch(null);
    onClose();
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      const values: any = await form.validateFields();
      values.accountId = user?.accountId;
      const [userName] = values.email.split("@");
      values.userName = userName;
      values.password = values.confirmPassword;

      await authService.createUser(values);
      handleClose();
      onSuccess();
    } catch (err: any) {
      if (err?.errorFields) return;
      const msg = err.response?.data?.message ?? err.message ?? "Failed to create user.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Add New User"
      open={open}
      onCancel={handleClose}
      width={500}
      footer={
        <>
          <Divider className={styles.modalDivider} />
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="primary" loading={loading} onClick={handleSubmit}>Create User</Button>
        </>
      }
      destroyOnClose
    >
      <Divider className={styles.modalDividerTop} />
      {error && (
        <div className={styles.modalError}>
          {Array.isArray(error)
            ? error.map((msg, i) => <div key={i}><Text>{msg}</Text></div>)
            : <Text>{error}</Text>}
        </div>
      )}
      <Form
        form={form}
        layout="vertical"
        requiredMark={(label, { required }) => (
          <>{label}{required && <span className={styles.requiredAsterisk}>*</span>}</>
        )}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: "" }]}>
              <Input placeholder="Enter first name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: "" }]}>
              <Input placeholder="Enter last name" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item label="Email" name="email" rules={[{ required: true, type: "email", message: "" }]}>
          <Input placeholder="Enter email address" />
        </Form.Item>

        <Form.Item
          label="Temporary Password"
          name="temporaryPassword"
          className={styles.formItemNoMargin}
          help={null}
          validateTrigger="onChange"
          rules={[{ required: true, message: "" }, {
            validator: (_, value) => {
              if (!value) return Promise.reject();
              const isValid = value.length >= 6 && /[a-z]/.test(value) && /[A-Z]/.test(value) && /\d/.test(value) && /[@$!%*?&]/.test(value);
              return isValid ? Promise.resolve() : Promise.reject();
            },
          }]}
        >
          <Input.Password
            placeholder="Enter temporary password"
            onChange={e => handlePasswordChange(e.target.value)}
            status={
              isPasswordTouched && !(passwordRules.length && passwordRules.lowercase && passwordRules.uppercase && passwordRules.number && passwordRules.special)
                ? "error" : ""
            }
          />
        </Form.Item>

        <div className={styles.passwordRules}>
          <div className={styles.fullRow}>
            <span className={getRuleClass(passwordRules.length)}><FontAwesomeIcon icon={getIcon(passwordRules.length)} /></span>
            <span>6 characters minimum</span>
          </div>
          <div className={styles.gridRow}>
            <div><span className={getRuleClass(passwordRules.lowercase)}><FontAwesomeIcon icon={getIcon(passwordRules.lowercase)} /></span><span>Lowercase letter</span></div>
            <div><span className={getRuleClass(passwordRules.uppercase)}><FontAwesomeIcon icon={getIcon(passwordRules.uppercase)} /></span><span>Uppercase letter</span></div>
            <div><span className={getRuleClass(passwordRules.number)}><FontAwesomeIcon icon={getIcon(passwordRules.number)} /></span><span>Number</span></div>
            <div><span className={getRuleClass(passwordRules.special)}><FontAwesomeIcon icon={getIcon(passwordRules.special)} /></span><span>Special character</span></div>
          </div>
        </div>

        <Form.Item
          label="Confirm Temporary Password"
          name="confirmPassword"
          dependencies={["temporaryPassword"]}
          className={styles.confirmPasswordItem}
          help={null}
          validateTrigger="onChange"
          rules={[{ required: true, message: "" }, {
            validator(_, value) {
              if (!value) return Promise.reject();
              return value === form.getFieldValue("temporaryPassword") ? Promise.resolve() : Promise.reject();
            },
          }]}
        >
          <Input.Password
            placeholder="Confirm temporary password"
            onChange={e => handleConfirmChange(e.target.value)}
            status={passwordMatch === false ? "error" : ""}
          />
        </Form.Item>
        <div className={styles.passwordMatch}>
          {passwordMatch === null ? null : passwordMatch ? (
            <p className={styles.ruleValid}><FontAwesomeIcon icon={faCheck} /> Passwords match</p>
          ) : (
            <p className={styles.ruleInvalid}><FontAwesomeIcon icon={faXmark} /> Passwords do not match</p>
          )}
        </div>
        <div className={styles.modalInfoText}>
          <FontAwesomeIcon icon={faCircleInfo} className={styles.modalInfoIcon} />
          <Text type="secondary">This password is temporary. The created user will be required to create a new password when they log in for the first time.</Text>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateUserModal;
